// "Vertrag bearbeiten": einen schon erzeugten Vertrag (HTML-Datei) erkennen,
// in den Editor laden und für das Hochladen bereinigen. Herausgelöst aus
// VertragsAutomationStudio.jsx (23.09.2026, reines Verschieben), damit Desktop
// ("Bearbeitung") und Handy denselben Weg gehen.
//
// Der Editor läuft dabei im Modus "customer-contract": Er liest nur die Datei
// selbst (kein gespeicherter Vorlagenstand, nichts im localStorage). Gespeichert
// wird nie in eine Vorlage - der bearbeitete Vertrag geht nur an den Deal.

import { decodeHtmlEntities } from './vertragsStand';

function getContractDefinition(contracts, contractKey) {
  return (contracts || []).find((contract) => contract.key === String(contractKey || '').trim()) || null;
}

function getVariantDefinition(contract, variantKey) {
  if (!contract) return null;
  return contract.variants.find((variant) => variant.key === String(variantKey || '').trim())
    || contract.variants[0]
    || null;
}

export function normalizeContractDetectionText(value) {
  return decodeHtmlEntities(String(value || ''))
    .replace(/<[^>]+>/g, ' ')
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function detectVariantKeyFromHtml(html, fileName = '') {
  const text = normalizeContractDetectionText(`${fileName} ${html || ''}`);
  return /\bdruckversion\b|\bdruck\b|\bprint version\b|\bprint-version\b/.test(text)
    ? 'druckversion'
    : 'ci';
}

/**
 * Welcher Vertrag ist das? Erst am Dateinamen, dann an den Angaben im Dokument,
 * zuletzt am Text. contracts: die Vertragsliste des Aufrufers.
 * @returns {{ contract, variant, reason: 'filename'|'dataset'|'content' } | null}
 */
export function detectContractFromHtml(html, fileName = '', contracts = []) {
  const fileText = normalizeContractDetectionText(fileName);
  for (const contract of contracts) {
    for (const variant of contract.variants) {
      const variantFile = normalizeContractDetectionText(variant.file.replace(/\.html?$/i, ''));
      if (variantFile && fileText.includes(variantFile)) {
        return { contract, variant, reason: 'filename' };
      }
    }
  }

  let doc = null;
  try {
    doc = new DOMParser().parseFromString(String(html || ''), 'text/html');
  } catch (_error) {
    doc = null;
  }

  const body = doc?.body || null;
  const datasetContractKey = body?.dataset?.contractKey || doc?.documentElement?.dataset?.contractKey || '';
  const datasetContract = getContractDefinition(contracts, datasetContractKey);
  if (datasetContract) {
    const variant = getVariantDefinition(datasetContract, body?.dataset?.variantKey || detectVariantKeyFromHtml(html, fileName));
    return { contract: datasetContract, variant, reason: 'dataset' };
  }

  const title = doc?.querySelector('title')?.textContent || '';
  const h1 = doc?.querySelector('h1')?.textContent || '';
  const bodyClass = body?.className || '';
  const bodyText = body?.textContent || '';
  const primaryText = normalizeContractDetectionText([
    fileName,
    title,
    h1,
    bodyClass,
  ].join(' '));
  const text = normalizeContractDetectionText([
    fileName,
    title,
    h1,
    bodyClass,
    bodyText.slice(0, 60000),
  ].join(' '));

  const hasAnyIn = (haystack, ...terms) => terms.some((term) => haystack.includes(term));
  const hasAllIn = (haystack, ...terms) => terms.every((term) => haystack.includes(term));
  const hasAny = (...terms) => hasAnyIn(text, ...terms);
  const hasAll = (...terms) => hasAllIn(text, ...terms);
  const primaryHasAny = (...terms) => hasAnyIn(primaryText, ...terms);
  const variantKey = detectVariantKeyFromHtml(`${title} ${bodyClass} ${fileName}`, fileName);

  let contractKey = '';
  if (primaryHasAny('projektrechte', 'projektrecht') && primaryHasAny('pv', 'photovoltaik')) {
    contractKey = 'projektrechte-pv';
  } else if (primaryHasAny('kleinspeicher') && primaryHasAny('pachtvertrag', 'vorabpacht') && primaryHasAny('einmalzahlung', 'einmalige', 'einmalig', 'vorabpacht')) {
    contractKey = 'pachtvertrag-kleinspeicher-ez';
  } else if (primaryHasAny('kleinspeicher') && primaryHasAny('pachtvertrag') && primaryHasAny('jaehrliche', 'jaehrlich', 'jahreszahlung', 'jahrliche', 'jahrlich')) {
    contractKey = 'pachtvertrag-kleinspeicher-jz';
  } else if (primaryHasAny('kaufvertrag') && primaryHasAny('kleinspeicher', 'schwarmspeicher')) {
    contractKey = 'kaufvertrag-kleinspeicher';
  } else if (primaryHasAny('dachnutzungsvertrag') && primaryHasAny('sanierung')) {
    contractKey = 'dachnutzungsvertrag-sanierung';
  } else if (primaryHasAny('flaechennutzungsvertrag', 'flaechennutzung', 'dachverpachtung', 'dachnutzung') && primaryHasAny('einmalzahlung', 'einmalige', 'einmalig')) {
    contractKey = 'pachtvertrag-dachverpachtung-ez';
  } else if (primaryHasAny('flaechennutzungsvertrag', 'flaechennutzung', 'dachverpachtung', 'dachnutzung') && primaryHasAny('jaehrliche', 'jaehrlich', 'jahreszahlung', 'jahrliche', 'jahrlich')) {
    contractKey = 'pachtvertrag-dachverpachtung-jz';
  } else if (hasAny('projektrechte', 'projektrecht') && hasAny('pv', 'photovoltaik')) {
    contractKey = 'projektrechte-pv';
  } else if (hasAny('kleinspeicher') && hasAny('pachtvertrag', 'vorabpacht') && hasAny('einmalzahlung', 'einmalige', 'einmalig', 'vorabpacht')) {
    contractKey = 'pachtvertrag-kleinspeicher-ez';
  } else if (hasAny('kleinspeicher') && hasAny('pachtvertrag') && hasAny('jaehrliche', 'jaehrlich', 'jahreszahlung', 'jahrliche', 'jahrlich')) {
    contractKey = 'pachtvertrag-kleinspeicher-jz';
  } else if (hasAny('kaufvertrag') && hasAny('kleinspeicher', 'schwarmspeicher')) {
    contractKey = 'kaufvertrag-kleinspeicher';
  } else if (hasAny('flaechennutzungsvertrag', 'flaechennutzung', 'dachverpachtung', 'dachnutzung') && hasAny('einmalzahlung', 'einmalige', 'einmalig')) {
    contractKey = 'pachtvertrag-dachverpachtung-ez';
  } else if (hasAny('flaechennutzungsvertrag', 'flaechennutzung', 'dachverpachtung', 'dachnutzung') && hasAny('jaehrliche', 'jaehrlich', 'jahreszahlung', 'jahrliche', 'jahrlich')) {
    contractKey = 'pachtvertrag-dachverpachtung-jz';
  } else if (hasAll('kleinspeicher', 'pachtvertrag')) {
    contractKey = 'pachtvertrag-kleinspeicher-jz';
  } else if (hasAny('flaechennutzungsvertrag', 'dachverpachtung', 'dachnutzung')) {
    contractKey = 'pachtvertrag-dachverpachtung-jz';
  }

  const contract = getContractDefinition(contracts, contractKey);
  if (!contract) return null;
  return {
    contract,
    variant: getVariantDefinition(contract, variantKey),
    reason: 'content',
  };
}

// Versteckt das Editor-Panel und sperrt contenteditable, damit im Automation-Modus nichts editiert werden kann.
function removeCustomerEditorChrome(doc) {
  if (!doc) return;
  doc.querySelectorAll('style#enpower-automation-readonly, style#enpower-editor-style').forEach((node) => node.remove());
  doc.querySelectorAll('.enpower-editor-panel, [data-html-export-ignore], [data-editor-handle]').forEach((node) => node.remove());
  Array.from(doc.querySelectorAll('script')).forEach((script) => {
    const src = script.getAttribute('src') || '';
    const text = script.textContent || '';
    const isEditor = /contract-editor\.js/i.test(src)
      || /ENPOWER_REQUEST_EXPORT|PASSUS_LIBRARY_KEY|enpower-editor-panel|STORAGE_PREFIX\s*=\s*['"]enpower-contract-editor:/i.test(text);
    const isReadonlyGuard = /enpower-automation-readonly|function\s+disable\(\)\{var p=document\.querySelector\("\.enpower-editor-panel"\)/i.test(text);
    if (isEditor || isReadonlyGuard) script.remove();
  });
}

// Hochgeladene Verträge kommen von außen (Mail-Anhang, vom Kunden zurück).
// Im Editor laufen sie als same-origin iframe - ein eingeschleustes Skript
// könnte dort das Anmelde-Token lesen oder mit den Rechten des Bearbeiters
// Vorlagen und Tool-Rechte ändern (Sicherheits-Review 23.09.2026). Deshalb
// bleibt nur, was die Vorlagen selbst mitbringen: der Editor-Stand als JSON
// und beim Lageplan der feste Ersatz für ein fehlendes Bild. Paginierung und
// Editor hängt prepareCustomerEditableHtml danach selbst wieder an.
const LAGEPLAN_ERSATZ = /^if\(!this\.dataset\.f\)\{this\.dataset\.f='1';this\.src='data:image\/svg\+xml;base64,[A-Za-z0-9+/=]+';\}$/;
// Browser überlesen Leer- und Steuerzeichen im Schema ("java\tscript:").
const GEFAEHRLICHE_URL = /^(javascript:|vbscript:|data:text\/html)/i;
const istGefaehrlicheUrl = (wert) => GEFAEHRLICHE_URL.test(String(wert || '').replace(/[\u0000- ]+/g, ''));
const URL_ATTRIBUTE = new Set(['href', 'src', 'xlink:href', 'action', 'formaction']);

function istErlaubterHandler(element, name, wert) {
  return name === 'onerror' && element.tagName === 'IMG' && LAGEPLAN_ERSATZ.test(String(wert || '').trim());
}

// `wurzel`: das Dokument oder der Inhalt eines <template>. querySelectorAll reicht
// nicht in template.content - die Seitenvorlage klont die Paginierung aber in jede
// Seite, ein onerror darin liefe im Editor (Review 25.10.2026).
function entschaerfeFremdesHtml(wurzel) {
  Array.from(wurzel.querySelectorAll('script')).forEach((skript) => {
    const istEditorStand = skript.id === 'enpower-editor-state' && /json/i.test(skript.getAttribute('type') || '');
    if (!istEditorStand) skript.remove();
  });
  wurzel.querySelectorAll('base, meta[http-equiv], iframe, frame, frameset, object, embed, applet, portal')
    .forEach((knoten) => knoten.remove());
  wurzel.querySelectorAll('*').forEach((element) => {
    Array.from(element.attributes).forEach(({ name, value }) => {
      const klein = name.toLowerCase();
      if (klein.startsWith('on')) {
        if (!istErlaubterHandler(element, klein, value)) element.removeAttribute(name);
        return;
      }
      if (URL_ATTRIBUTE.has(klein) && istGefaehrlicheUrl(value)) element.removeAttribute(name);
    });
  });
  wurzel.querySelectorAll('template').forEach((vorlage) => entschaerfeFremdesHtml(vorlage.content));
}

function ensureScript(doc, src) {
  if (!doc || !src) return;
  const wanted = String(src).replace(/^\.\//, '');
  const exists = Array.from(doc.querySelectorAll('script[src]')).some((script) => (
    (script.getAttribute('src') || '').replace(/^\.\//, '') === wanted
  ));
  if (exists) return;
  const script = doc.createElement('script');
  script.src = src;
  (doc.body || doc.documentElement).appendChild(script);
}

// Mit „Seite speichern unter“ gesicherte Verträge (Befund 25.10.2026, Kleinspeicher
// Deal 11470, 5,5 MB): Chrome kommentiert dabei das <base> aus
// („<!--<base href="/contracts/">-->“) und speichert den Inhalt von <template> nicht
// mit. entschaerfeFremdesHtml entfernt das echte <base>; der Kommentar blieb und hielt
// injectBaseHref davon ab, ein neues einzusetzen - Paginierung und Editor luden nicht.
// Und ohne Seitenvorlage lässt sich nicht paginieren: Die Datei zeigte nur ihre
// mitgespeicherten Seiten, nach dem ersten Export (Einfügen, Deal-Daten aktualisieren,
// Entwurf wiederherstellen) nur noch das Deckblatt - der Vertrag war vollständig da,
// aber unsichtbar.
const SEITENVORLAGE = /<template\b[^>]*\bid=(["'])contract-page-template\1[^>]*>([\s\S]*?)<\/template>/i;
const SEITEN_KLASSE = /\bclass=(["'])(?:[^"']*\s)?contract-page(?:\s[^"']*)?\1/i;

/** Hat das HTML eine Seitenvorlage, aus der die Paginierung Seiten bauen kann? */
export function hatBrauchbareSeitenvorlage(html) {
  const treffer = SEITENVORLAGE.exec(String(html || ''));
  return Boolean(treffer && SEITEN_KLASSE.test(treffer[2]));
}

/** Die Seite aus der Seitenvorlage einer Vertragsdatei (leer, wenn es keine brauchbare gibt). */
export function seitenvorlageAus(html) {
  const treffer = SEITENVORLAGE.exec(String(html || ''));
  return treffer && SEITEN_KLASSE.test(treffer[2]) ? treffer[2].trim() : '';
}

/** Keine brauchbare Seitenvorlage und auch keine gerenderte Seite, aus der sie sich nachbauen ließe? */
export function seitenvorlageFehltGanz(html) {
  return !hatBrauchbareSeitenvorlage(html) && !SEITEN_KLASSE.test(String(html || ''));
}

function entferneBaseKommentare(doc) {
  const gang = doc.createTreeWalker(doc, NodeFilter.SHOW_COMMENT);
  const kommentare = [];
  while (gang.nextNode()) {
    if (/<base\b/i.test(gang.currentNode.nodeValue || '')) kommentare.push(gang.currentNode);
  }
  kommentare.forEach((kommentar) => kommentar.remove());
}

// Erst aus der ersten mitgespeicherten Seite der Datei (Kopf- und Fußzeile gehören
// zu diesem Vertrag), sonst aus `ersatz` (Seitenvorlage der Originaldatei).
function stelleSeitenvorlageHer(doc, ersatz = '') {
  let vorlage = doc.getElementById('contract-page-template');
  if (vorlage?.content?.querySelector('.contract-page')) return;
  let inhalt = '';
  const gerendert = doc.querySelector('#contract-pages .contract-page');
  if (gerendert?.querySelector('.page-body')) {
    const seite = gerendert.cloneNode(true);
    seite.querySelector('.page-body').innerHTML = '';
    // Was die Paginierung je Seite ergänzt (contract-pagination.js).
    seite.querySelectorAll('[data-page-number], [data-standard-signature-overlay]').forEach((knoten) => knoten.remove());
    seite.classList.remove('pagination-overflow');
    inhalt = seite.outerHTML;
  } else if (ersatz) {
    inhalt = ersatz;
  }
  if (!inhalt) return;
  if (!vorlage) {
    const seiten = doc.getElementById('contract-pages');
    if (!seiten) return;
    vorlage = doc.createElement('template');
    vorlage.id = 'contract-page-template';
    seiten.parentNode.insertBefore(vorlage, seiten);
  }
  vorlage.innerHTML = inhalt;
}

/**
 * Vertrag für den Editor im Modus "customer-contract" vorbereiten. Wirft ohne #contract-source.
 * `seitenvorlage`: Ersatz, falls die Datei weder Seitenvorlage noch gerenderte Seiten hat.
 */
export function prepareCustomerEditableHtml(html, { contractKey = '', variantKey = '', seitenvorlage = '' } = {}) {
  if (typeof html !== 'string' || !html.trim()) {
    throw new Error('Die HTML-Datei ist leer.');
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const source = doc.getElementById('contract-source');
  if (!source) {
    throw new Error('Diese HTML-Datei enthaelt keine Vertragsstruktur (#contract-source).');
  }

  removeCustomerEditorChrome(doc);
  entschaerfeFremdesHtml(doc);
  entferneBaseKommentare(doc);
  stelleSeitenvorlageHer(doc, seitenvorlage);
  if (doc.body) {
    doc.body.dataset.editorScope = 'customer-contract';
    doc.body.dataset.contractKey = contractKey || 'customer-contract';
    doc.body.dataset.variantKey = variantKey || '';
    doc.body.classList.remove('editor-mode', 'editor-move-mode');
  }
  doc.querySelectorAll('[contenteditable]').forEach((node) => node.removeAttribute('contenteditable'));
  doc.querySelectorAll('[draggable]').forEach((node) => node.removeAttribute('draggable'));
  doc.querySelectorAll('.editor-selected,.editor-multi-selected,.editor-dragging,.editor-drop-before,.editor-drop-after').forEach((node) => {
    node.classList.remove('editor-selected', 'editor-multi-selected', 'editor-dragging', 'editor-drop-before', 'editor-drop-after');
  });
  ensureScript(doc, 'contract-pagination.js?v=tool-pagination-20260615-page-numbers1');
  ensureScript(doc, 'contract-editor.js?v=tool-editor-20260923-mobil1');
  return '<!doctype html>\n' + doc.documentElement.outerHTML;
}

/** Bearbeiteten Vertrag aus dem Editor-Dokument lösen: ohne Editor, Seiten leer, Paginierung dabei. */
export function cleanCustomerEditableHtmlForExport(doc) {
  if (!doc || !doc.documentElement) {
    throw new Error('Editor-Inhalt konnte nicht gelesen werden.');
  }

  const exportedDoc = doc.documentElement.cloneNode(true);
  removeCustomerEditorChrome({ querySelectorAll: exportedDoc.querySelectorAll.bind(exportedDoc) });
  const generatedPages = exportedDoc.querySelector('#contract-pages');
  if (generatedPages) {
    generatedPages.innerHTML = '';
  }
  exportedDoc.querySelectorAll('[contenteditable]').forEach((node) => node.removeAttribute('contenteditable'));
  exportedDoc.querySelectorAll('[draggable]').forEach((node) => node.removeAttribute('draggable'));
  exportedDoc.querySelectorAll('.editor-selected,.editor-multi-selected,.editor-dragging,.editor-drop-before,.editor-drop-after').forEach((node) => {
    node.classList.remove('editor-selected', 'editor-multi-selected', 'editor-dragging', 'editor-drop-before', 'editor-drop-after');
  });
  const body = exportedDoc.querySelector('body');
  if (body) {
    body.classList.remove('editor-mode', 'editor-move-mode');
    delete body.dataset.editorScope;
    delete body.dataset.contractKey;
    delete body.dataset.variantKey;
  }
  const existingPagination = Array.from(exportedDoc.querySelectorAll('script[src]')).some((script) => (
    /contract-pagination\.js/i.test(script.getAttribute('src') || '')
  ));
  if (!existingPagination) {
    const script = doc.createElement('script');
    script.src = 'contract-pagination.js?v=tool-pagination-20260615-page-numbers1';
    (exportedDoc.querySelector('body') || exportedDoc).appendChild(script);
  }
  return '<!doctype html>\n' + exportedDoc.outerHTML;
}

// Blöcke des Vertrags (#contract-source) ohne eingefügte Passus - dieselbe Zählung
// wie vertragsBloecke in api/_lib/klauselnEinfuegen.js. Ein erneut eingefügter
// Passus ersetzt seine alte Fassung; die darf kürzer sein.
const VERTRAGS_BLOECKE = 'p, h1, h2, h3, h4, h5, h6, li, table, figure, img';

/** Zahl der Vertragsblöcke; -1, wenn das HTML keinen Vertrag (#contract-source) enthält. */
export function vertragsBloeckeIm(html) {
  const doc = new DOMParser().parseFromString(String(html || ''), 'text/html');
  const quelle = doc.getElementById('contract-source');
  if (!quelle) return -1;
  return Array.from(quelle.querySelectorAll(VERTRAGS_BLOECKE))
    .filter((knoten) => !knoten.hasAttribute('data-optional-clause-id'))
    .length;
}

/**
 * Leer, wenn `nachher` alles vom Vertrag `vorher` enthält; sonst der Grund.
 *
 * Seit 25.10.2026: Nach „Freigegebene Ergänzung in den Vertrag einfügen“ stand
 * vom hochgeladenen Vertrag nur noch das Deckblatt, und der Editor zeigte das
 * gekürzte Ergebnis an. Was vom Server zurückkommt, ersetzt den Vertrag im Editor
 * deshalb nur noch, wenn nicht deutlich etwas fehlt - mehr als 3 Blöcke und mehr
 * als ein Zehntel, wie deutlichGekuerzt im Server: Der Lageplan-Weg geht noch
 * durch contracts-am, der einen Absatz mit leerem Wert weglassen könnte.
 */
export function vertragUnvollstaendig(vorher, nachher) {
  const bisher = vertragsBloeckeIm(vorher);
  if (bisher <= 0) return '';
  const jetzt = vertragsBloeckeIm(nachher);
  const seitenvorlage = (html) => /\bid=(["'])contract-page-template\1/i.test(String(html || ''));
  const gekuerzt = jetzt < 0 || bisher - jetzt > Math.max(3, Math.floor(bisher / 10));
  if (gekuerzt || (seitenvorlage(vorher) && !seitenvorlage(nachher))) {
    return 'Der Vertrag kam unvollständig zurück und wurde nicht übernommen – im Editor ist nichts verändert.';
  }
  return '';
}

/**
 * Ist der Vertrag im Editor-iframe fertig eingelesen? Ein Export aus einem halb
 * eingelesenen Dokument enthält das Deckblatt, aber noch keine Vertragsquelle - in der
 * Datei vom 25.10.2026 (5,5 MB) beginnt #contract-source erst bei Zeichen 4,9 Mio.,
 * davor stehen die mitgespeicherten Seiten. Das frische iframe ist anfangs außerdem
 * ein leeres about:blank.
 */
export function editorBereit(iframe) {
  const doc = iframe?.contentDocument;
  return Boolean(doc && doc.readyState !== 'loading' && doc.getElementById('contract-source'));
}

export const EDITOR_LAEDT_NOCH = 'Der Vertrag wird im Editor noch geladen – bitte einen Moment warten und es dann erneut versuchen.';

/**
 * Den bearbeiteten Vertrag aus dem Editor-iframe lesen (wie readCurrentContractHtml
 * im Studio): erst den Editor seinen Stand schreiben lassen, dann bereinigen.
 * Wirft, solange der Vertrag noch nicht fertig eingelesen ist.
 */
export function leseKundenvertragAusRahmen(iframe) {
  if (!iframe || !iframe.contentDocument) {
    throw new Error('Der Editor ist nicht verfügbar.');
  }
  if (!editorBereit(iframe)) {
    throw new Error(EDITOR_LAEDT_NOCH);
  }
  const win = iframe.contentWindow;
  if (win && typeof win.__ENPOWER_GET_EDITOR_EXPORT === 'function') {
    win.__ENPOWER_GET_EDITOR_EXPORT();
  }
  return cleanCustomerEditableHtmlForExport(iframe.contentDocument);
}
